import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { GlobalService } from 'src/app/app-core/lib/global.service';
import { DigitalFormService } from 'src/app/app-core/services/digital-form.service';
import { IDigitalFormRequestInfo } from 'src/app/interfaces/digital-form';

/**
 * "Encaminhar para assinatura": o equivalente do "Aprovar e Tramitar" para formulário
 * digital com assinatura do responsável. A triagem não escolhe o departamento — ele
 * vem do formulário —, só confere para onde o processo vai e quem vai assinar.
 */
@Component({
    selector: 'app-forward-to-signers',
    templateUrl: './forward-to-signers.component.html',
    styleUrls: ['./forward-to-signers.component.scss'],
    providers: [DigitalFormService],
})
export class ForwardToSignersComponent implements OnChanges, OnDestroy {
    /** Nome do modal, aberto pelo pai com `global.showModalInfo(modalName)`. */
    @Input() modalName: string;
    @Input() solicitacaoId: number | null = null;

    /** Mensagem de sucesso da API, para o pai exibir antes de navegar. */
    @Output() forwarded = new EventEmitter<string>();

    public info: IDigitalFormRequestInfo | null = null;
    public nota = '';
    public loading = false;
    public sending = false;
    public error: string | null = null;

    private destroy$ = new Subject<void>();

    constructor(
        private global: GlobalService,
        private digitalFormService: DigitalFormService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.solicitacaoId && this.solicitacaoId) {
            this.load();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get canSubmit(): boolean {
        return !this.sending && !!this.info?.destino && (this.info?.assinantes?.length || 0) > 0;
    }

    private load(): void {
        this.loading = true;
        this.error = null;

        this.digitalFormService
            .getForRequest(this.solicitacaoId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.info = response?.requerimento || null;
                    this.loading = false;

                    if (this.info && !this.info.destino) {
                        this.error = 'O formulário não tem um departamento de destino. Peça ao administrador para configurá-lo.';
                    } else if (this.info && this.info.assinantes.length === 0) {
                        this.error = 'O formulário não tem nenhum responsável ativo. Peça ao administrador para revisar as permissões.';
                    }
                },
                error: () => {
                    this.loading = false;
                    this.error = 'Não foi possível carregar os dados do requerimento.';
                },
            });
    }

    public submit(): void {
        if (!this.canSubmit) return;

        this.sending = true;
        this.error = null;

        this.digitalFormService
            .forwardToSigners(this.solicitacaoId, this.nota.trim() || null)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.sending = false;
                    this.global.closeModalInfo(this.modalName);
                    this.forwarded.emit(response?.message);
                },
                error: (err) => {
                    this.sending = false;
                    this.error = err?.error?.message || 'Não foi possível encaminhar a solicitação.';
                },
            });
    }
}
